import AmpEvents, { AmpEvent } from '@amplify/amp-events';
import kebabCase from 'lodash.kebabcase';

import { getEnv } from 'shared/config/constants';
import { AuthService } from 'shared/lib/keycloak';
import { loggerService } from 'shared/lib/logger';
import { isIpad } from 'shared/lib/utils';

import {
  XApiEventKeys,
  XApiResultExtensions,
  XApiContextExtensions,
} from './types';

class EventService {
  userId = '';
  ampEvents: AmpEvents | null = null;
  events: Record<XApiEventKeys, AmpEvent | undefined> | null = null;

  init(userId: string) {
    this.userId = userId;
    this.ampEvents = new AmpEvents({
      [AmpEvents.OPTIONS.APP_NAME]: 'educator-home',
      [AmpEvents.OPTIONS.APP_VERSION]: '1.0.0',
      [AmpEvents.OPTIONS.ENVIRONMENT]: getEnv(),
      [AmpEvents.OPTIONS.USER_ID]: userId,
    });

    // Avoid formatting of the listed keys.
    this.ampEvents.updateFormattingAllowList({
      context: {
        extensions: {
          keys: ['activity-id', 'edition-id', 'instance-id'],
        },
      },
    });

    this.events = {
      [XApiEventKeys.viewed]: this.createEvent('viewed', 'educator-home'),
      [XApiEventKeys.openedAvatarBuilder]: this.createEvent(
        'opened',
        'avatar-builder',
      ),
      [XApiEventKeys.programsListExpanded]: this.createEvent(
        'expanded',
        'programs-list',
      ),
      [XApiEventKeys.programsListCollapsed]: this.createEvent(
        'collapsed',
        'programs-list',
      ),
      [XApiEventKeys.programSelected]: this.createEvent('selected', 'program'),
      [XApiEventKeys.classFiltered]: this.createEvent('filtered', 'class'),
      [XApiEventKeys.goToLessonSelected]: this.createEvent(
        'selected',
        'go-to-lesson',
      ),
      [XApiEventKeys.viewWork]: this.createEvent('selected', 'view-work'),
      [XApiEventKeys.assignmentUnassignSelected]: this.createEvent(
        'unassigned',
        'amplify-assign',
      ),
      [XApiEventKeys.assignmentEditSelected]: this.createEvent(
        'selected',
        'edit',
      ),
      [XApiEventKeys.productBreadcrumbSelected]: this.createEvent(
        'selected',
        'product-breadcrumb',
      ),
      [XApiEventKeys.recommendationProductBreadcrumbSelected]: this.createEvent(
        'selected',
        'product-breadcrumb',
      ),
      [XApiEventKeys.unitBreadcrumbSelected]: this.createEvent(
        'selected',
        'unit-breadcrumb',
      ),
      [XApiEventKeys.lessonBreadcrumbSelected]: this.createEvent(
        'selected',
        'lesson-breadcrumb',
      ),
      [XApiEventKeys.sectionInfoHovered]: this.createEvent('hovered', 'info'),
      [XApiEventKeys.seeAllButtonSelected]: this.createEvent(
        'selected',
        'see-all',
      ),
      [XApiEventKeys.backButtonSelected]: this.createEvent('selected', 'back'),
      [XApiEventKeys.openedRecommendation]: this.createEvent(
        'opened',
        'recommendation',
      ),
      [XApiEventKeys.productFiltered]: this.createEvent('filtered', 'product'),
      [XApiEventKeys.assignmentReassignSelected]: this.createEvent(
        'reassigned',
        'amplify-assign',
      ),
      [XApiEventKeys.selectedUndoLessonUnassign]: this.createEvent(
        'undid-unassign',
        'amplify-assign',
      ),
      [XApiEventKeys.selectedUndoLessonReassign]: this.createEvent(
        'undid-reassign',
        'amplify-assign',
      ),
      [XApiEventKeys.selectedUpdateAssignment]: this.createEvent(
        'edited',
        'amplify-assign',
      ),
      [XApiEventKeys.selectedLiteracyInsightsTooltip]: this.createEvent(
        'selected',
        'insight-tooltip',
      ),
      [XApiEventKeys.selectedTLiteracyInsightsTooltipLink]: this.createEvent(
        'selected',
        'insight-tooltip-link',
      ),
      [XApiEventKeys.selectedLiteracyInsightsModalOption]: this.createEvent(
        'selected',
        'recommendation-option',
      ),
      [XApiEventKeys.selectedLiteracyInsightsModalLink]: this.createEvent(
        'selected',
        'recommendation-modal-link',
      ),
      [XApiEventKeys.closedLiteracyInsightsModal]: this.createEvent(
        'selected',
        'recommendation-modal-close-button',
      ),
      [XApiEventKeys.launchedWalkthrough]: this.createEvent(
        'launched',
        'educator-home-walkthrough',
      ),
      [XApiEventKeys.selectedQuickLink]: this.createEvent(
        'selected',
        'quick-link',
      ),
    };
  }

  createEvent(verb: string, object: string) {
    if (!this.ampEvents) return;

    return this.ampEvents.eventFactory
      .create()
      .addActorAccount('name', this.userId)
      .addVerbId(verb)
      .addVerb('display', { 'en-US': verb })
      .addObjectId(object)
      .addContextExtension('session-id', AuthService.getSessionId())
      .addContextExtension('app-name', 'educator-home')
      .addContextExtension(
        'application-version',
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        kebabCase(import.meta.env.VITE_APP_VERSION) ?? '0-1-0',
      )
      .addContextExtension('is-ipad', isIpad());
  }

  addResultExtensions(
    event: AmpEvent | undefined,
    extensions: XApiResultExtensions[keyof XApiResultExtensions]['result'],
  ) {
    Object.entries(extensions).forEach(([key, value]) => {
      event?.addResultExtension(kebabCase(key), value);
    });
    return event;
  }

  addContextExtensions(
    event: AmpEvent | undefined,
    extensions: XApiContextExtensions[keyof XApiContextExtensions]['result'],
  ) {
    Object.entries(extensions).forEach(([key, value]) => {
      event?.addContextExtension(kebabCase(key), value);
    });
    return event;
  }

  generateEvent(
    type: XApiEventKeys,
    resultExtensions?: XApiResultExtensions[keyof XApiResultExtensions],
    contextExtensions?: XApiContextExtensions[keyof XApiContextExtensions],
  ) {
    const xAPIEvent = this.events?.[type];

    if (!xAPIEvent) return;

    if (resultExtensions?.result) {
      this.addResultExtensions(xAPIEvent, resultExtensions.result);
    }

    if (contextExtensions?.result) {
      this.addContextExtensions(xAPIEvent, contextExtensions.result);
    }

    return xAPIEvent;
  }

  send(
    type: XApiEventKeys,
    resultExtensions?: XApiResultExtensions[keyof XApiResultExtensions],
    contextExtensions?: XApiContextExtensions[keyof XApiContextExtensions],
  ): Promise<unknown> {
    if (getEnv() === 'local') return Promise.resolve();

    const xAPIEvent = this.generateEvent(
      type,
      resultExtensions,
      contextExtensions,
    );

    if (xAPIEvent === undefined) {
      loggerService.logError('xAPI event service is not initiated');
      return Promise.resolve();
    }

    return xAPIEvent.emit();
  }
}

export const eventService = new EventService();
